import { browser } from '$app/env';
import { ProductApi } from '#api/productApi.ts';
import { writable } from 'svelte/store';
import { SystemApi, type Role } from '#api/systemApi.ts';
import type { HubSpotConfig } from '#api/utility.ts';
import { invalidateClientStatuses } from '#lib/services/clientStatuses.ts';

export const search = writable('');
export const version = writable<string | undefined>();
export const environment = writable<string | undefined>();
export const isProd = writable(true);
export const hubSpotConfig = writable<HubSpotConfig | undefined>();

export type AuthStatus = 'pending' | 'authenticated' | 'unauthenticated';

export const authStatus = writable<AuthStatus>('pending');

export const products = writable<{ id: number; clientId: number; name: string }[]>([]);
export const fullscreen = writable(false);
export const roles = writable<Role[]>([]);

let rolesInitialized = false;
let eventsRegistered = false;
const loadedClientIds = new Set<number>();
const loadingClientIds = new Set<number>();

export function resetAuthenticatedStores() {
    rolesInitialized = false;
    loadedClientIds.clear();
    loadingClientIds.clear();
    products.set([]);
    roles.set([]);
    invalidateClientStatuses();
}

export async function ensureProductsForClient(clientId: number) {
    if (!clientId || loadedClientIds.has(clientId) || loadingClientIds.has(clientId)) return;

    loadingClientIds.add(clientId);

    try {
        const productApi = new ProductApi();
        const productResult = await productApi.list({ clientId, limit: 9999 });
        const newProducts = productResult.data!.results.map((c) => ({
            id: c.id,
            clientId: c.clientId!,
            name: c.name
        }));

        products.update((s) => {
            const existing = s.filter((p) => p.clientId != clientId);
            return [...existing, ...newProducts];
        });

        loadedClientIds.add(clientId);
    } catch {
        // allow retry
    } finally {
        loadingClientIds.delete(clientId);
    }
}

export async function initAuthenticatedStores() {
    if (!eventsRegistered && browser) {
        eventsRegistered = true;
        document.addEventListener('ConcordServicing.Common.Events.ProductCreated', productCreated as EventListener);
        document.addEventListener('ConcordServicing.Common.Events.ProductUpdated', productUpdated as EventListener);
        document.addEventListener('ConcordServicing.Common.Events.ProductDeleted', productDeleted as EventListener);
    }

    if (rolesInitialized) return;
    rolesInitialized = true;

    try {
        const systemApi = new SystemApi();
        const result = await systemApi.getRoles();
        roles.set(result.data!.roles);
    } catch {
        rolesInitialized = false;
    }
}

function productUpdated({ detail }: CustomEvent<{ id: number; name: string }>) {
    products.update((s) => {
        const product = s.find((c) => c.id == detail.id);

        if (product) product.name = detail.name;

        return s;
    });
}

function productCreated({ detail }: CustomEvent<{ id: number; clientId: number; name: string }>) {
    products.update((s) => {
        s.push({ id: detail.id, clientId: detail.clientId, name: detail.name });

        return s;
    });
}

function productDeleted({ detail }: CustomEvent<{ id: number; name: string }>) {
    products.update((s) => {
        return s.filter((c) => c.id != detail.id);
    });
}
